import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, Phone, Users, CalendarClock, CheckSquare, Repeat, Wrench } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { WorkedDialog } from "@/components/activities/WorkedDialog";
import { fmtDay, fmtTime, isToday } from "@/lib/format";
import { Panel, SectionLabel, StatusBadge } from "@/components/common/ui-kit";

const TYPES = {
  call: { label: "Chiamata", icon: Phone }, meeting: { label: "Riunione", icon: Users },
  appointment: { label: "Appuntamento", icon: CalendarClock }, task: { label: "Attività", icon: CheckSquare },
  follow_up: { label: "Follow-up", icon: Repeat }, intervention: { label: "Intervento", icon: Wrench },
};

const Done = ({ it, i, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState(false);
  const go = async () => {
    if (it.intervention_id) { setAsk(true); return; }
    setBusy(true);
    try { await api.post(`/activities/${it.id}/complete`, {}); toast.success("Attività completata"); onDone?.(); }
    catch (e) { toast.error(errorMessage(e)); setBusy(false); }
  };
  return (
    <>
    <button onClick={go} disabled={busy} aria-label="Completa" title="Segna come completata" data-testid={`upcoming-complete-${i}`}
      className="mt-2 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 border-slate-300 text-transparent transition-colors hover:border-emerald-500 hover:text-emerald-500 disabled:opacity-50"><Check className="h-3.5 w-3.5" /></button>
    <WorkedDialog a={ask ? { id: it.id, title: it.title, worked_minutes: it.worked_minutes } : null} complete onClose={() => setAsk(false)} onDone={() => onDone?.()} />
    </>
  );
};

const Row = ({ it, i, onDone }) => {
  const { can } = useAuth();
  const t = TYPES[it.type] || TYPES.task;
  const Icon = t.icon;
  return (
    <li className="relative flex items-start gap-2 pl-8">
      <span className="absolute left-0 top-1 flex h-6 w-6 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500">
        <Icon className="h-3 w-3" />
      </span>
      <Link to={it.path} className="block min-w-0 flex-1 rounded-lg px-3 py-2 transition-colors hover:bg-slate-50" data-testid={`upcoming-item-${i}`}>
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-xs font-semibold text-slate-900">{isToday(it.at) ? "Oggi" : fmtDay(it.at)} · {fmtTime(it.at)}</span>
          <StatusBadge tone={it.kind === "intervention" ? "amber" : "blue"} dot={false}>{t.label}</StatusBadge>
        </div>
        <p className="mt-1 text-sm font-medium text-slate-800">{it.title}</p>
        <p className="text-xs text-slate-500">{[it.company, it.assignee].filter(Boolean).join(" · ")}</p>
      </Link>
      {it.kind === "activity" && can("activities:complete") && <Done it={it} i={i} onDone={onDone} />}
    </li>
  );
};

export const UpcomingTimeline = ({ items, onDone }) => (
  <Panel className="animate-rise delay-3 p-5" data-testid="dashboard-section-prossime-attivita">
    <SectionLabel>Prossime attività</SectionLabel>
    <h2 className="mt-1 font-display text-lg font-bold text-slate-900">Cosa è pianificato</h2>
    {items.length === 0 ? (
      <p className="mt-6 text-sm text-slate-500" data-testid="upcoming-empty">Nessuna attività pianificata.</p>
    ) : (
      <ol className="relative mt-5 space-y-2 before:absolute before:bottom-2 before:left-3 before:top-2 before:w-px before:bg-slate-200">
        {items.map((it, i) => <Row key={it.id} it={it} i={i} onDone={onDone} />)}
      </ol>
    )}
  </Panel>
);
